import { useAuth0 } from '@auth0/auth0-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Suspense, lazy, useEffect } from 'react';
import 'react-loading-skeleton/dist/skeleton.css';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeUIStyleObject } from 'theme-ui';
import { Provider as UrqlProvider } from 'urql';
import CustomModal from './components/Modal';
import ErrorBoundary from './components/ErrorBoundary';
import GoogleMapsScript from './components/GoogleMapsScript';
import Navbar, {
  analyticsPath,
  deliveryNotesPath,
  deliveryOverridesCreatePath,
  deliveryOverridesPath,
  deliveryPath,
  dockBookingPublicPath,
  dockBookingsPath,
  dockCheckinPublicPath,
  dockCheckinWarehousePublicPath,
  dockDepartedPublicPath,
  dockDepartedWarehousePublicPath,
  dockDisplayPublicPath,
  driverSafetyAcknowledgementsCreatePath,
  driverSafetyAcknowledgementsDetailPath,
  driverSafetyAcknowledgementsPath,
  fulfillmentDeltasPath,
  fulfillmentDeltasPrintablePath,
  invoiceByDeliveryDetailPath,
  invoiceByDeliveryPath,
  issuesPath,
  itemSearchPath,
  locationsPath,
  loginPath,
  optimizationComparePath,
  optimizationNewPath,
  optimizationPath,
  packingListPath,
  pickingLabelsPath,
  processPathAssociatesSetupPath,
  processPathSelfAssignPublicPath,
  processPathSettingsPath,
  processPathSwitchPublicPath,
  processPathsPath,
  putawayAppPath,
  putawaySettingsPath,
  putawayReportPath,
  stageGatePath,
  vendorPortalPublicPath,
  vendorReceivingPath,
  warehouseLayoutPath,
  warehouseLayoutBuilderPath,
  warehouseOperationsPath,
  vehicleInspectionReportPath,
  vehicleInspectionReportsPath,
  vehicleInspectionTruckPath,
} from './components/Navbar';
import ToastNotification from './components/ToastNotification';
import ProtectedRoute from './components/ProtectedRoute';
import config from './config';
import { isPutawayOnlyUser } from './utils/authRoles';
import { isPwaContext } from './utils/pwa';
import { client } from './graphql/client';
import { publicClient } from './graphql/publicClient';
import { useAppSelector } from './hooks/store';
import { useLockToPutaway } from './hooks/useLockToPutaway';
import { useOutboundGatesEnabled } from './hooks/useOutboundGatesEnabled';
import { useWarehouseLayoutBuilderEnabled } from './hooks/useWarehouseLayoutBuilderEnabled';
import FulfillmentDeltasPrintable from './pages/FulfillmentDeltasPrintable';
import ItemSearch from './pages/ItemSearch';
import Locations from './pages/Locations';
import PickingLabels from './pages/PickingLabels';
import { getOptimizationRouteMode } from './pages/Optimization/routeMode';
import analytics from './services/analytics/index';
import AnalyticsTrackClicks from './services/analytics/TrackClicks';
import { sec } from './services/authService';
import { authSelector } from './state/selectors/auth';

const GOOGLE_MAPS_API_KEY = config().google_maps_api_key;

// Use React lazy to code-split our pages
const Login = lazy(() => import('./pages/Login'));
const RouteManagement = lazy(() => import('./pages/Delivery'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Issues = lazy(() => import('./pages/Issues'));
const InvoiceByDelivery = lazy(() => import('./pages/InvoiceByDelivery'));
const InvoiceByDeliveryDetail = lazy(
  () => import('./pages/InvoiceByDelivery/Detail'),
);
const WarehouseOperations = lazy(() => import('./pages/WarehouseOperations'));
const Optimization = lazy(() => import('./pages/Optimization'));
const OptimizationLegacy = lazy(() => import('./pages/Optimization/Legacy'));
const OptimizationNew = lazy(() => import('./pages/Optimization/New'));
const OptimizationCompare = lazy(() => import('./pages/Optimization/Compare'));
const VendorReceiving = lazy(() => import('./pages/VendorReceiving'));
const FulfillmentDeltas = lazy(() => import('./pages/FulfillmentDeltas'));
const PackingList = lazy(() => import('./pages/PackingList'));
const DeliveryNotes = lazy(() => import('./pages/DeliveryNotes'));
const DriverSafetyAcknowledgements = lazy(
  () => import('./pages/DriverSafetyAcknowledgements'),
);
const CreateDriverSafetyAcknowledgement = lazy(
  () => import('./pages/DriverSafetyAcknowledgements/Create'),
);
const ViewDriverSafetyAcknowledgement = lazy(
  () => import('./pages/DriverSafetyAcknowledgements/Detail'),
);
const VehicleInspectionReports = lazy(
  () => import('./pages/VehicleInspectionReports'),
);
const VehicleInspectionTruck = lazy(
  () => import('./pages/VehicleInspectionReports/Truck'),
);
const VehicleInspectionReport = lazy(
  () => import('./pages/VehicleInspectionReports/Report'),
);
const DockBooking = lazy(() => import('./pages/DockBooking'));
const VendorPortal = lazy(() => import('./pages/VendorPortal'));
const DockDisplay = lazy(() => import('./pages/DockDisplay'));
const DockCheckin = lazy(() => import('./pages/DockCheckin'));
const DockDeparted = lazy(() => import('./pages/DockDeparted'));
const DockBookingAdmin = lazy(() => import('./pages/DockBookingAdmin'));
const ProcessPathAdmin = lazy(() => import('./pages/ProcessPathAdmin'));
const ProcessPathSettings = lazy(() => import('./pages/ProcessPathSettings'));
const ProcessPathAssociatesSetup = lazy(
  () => import('./pages/ProcessPathAssociatesSetup'),
);
const ProcessPathSelfAssign = lazy(
  () => import('./pages/ProcessPathSelfAssign'),
);
const ProcessPathSwitch = lazy(() => import('./pages/ProcessPathSwitch'));
const PutawayReport = lazy(() => import('./pages/PutawayReport'));
const PutawayApp = lazy(() => import('./pages/PutawayCompanion/Route'));
const PutawaySettings = lazy(() => import('./pages/PutawaySettings'));
const WarehouseLayout = lazy(() => import('./pages/WarehouseLayout'));
const WarehouseLayoutBuilder = lazy(
  () => import('./pages/WarehouseLayout/Builder'),
);
const StageGate = lazy(() => import('./pages/StageGate'));
const DeliveryOverrides = lazy(() => import('./pages/DeliveryOverrides'));
const CreateDeliveryOverride = lazy(
  () => import('./pages/DeliveryOverrides/Create'),
);

const mainStyle: ThemeUIStyleObject = {
  height: '100vh',
};

const getRouteManagementPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <RouteManagement />;
  return <ProtectedRoute Component={RouteManagement} />;
};

const getAnalyticsPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <Analytics />;
  return <ProtectedRoute Component={Analytics} />;
};

const getIssuesPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <Issues />;
  return <ProtectedRoute Component={Issues} />;
};

const getInvoiceByDeliveryPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <InvoiceByDelivery />;
  return <ProtectedRoute Component={InvoiceByDelivery} />;
};

const getInvoiceByDeliveryDetailPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <InvoiceByDeliveryDetail />;
  return <ProtectedRoute Component={InvoiceByDeliveryDetail} />;
};

const getVendorReceivingPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <VendorReceiving />;
  return <ProtectedRoute Component={VendorReceiving} />;
};

const getWarehouseOperationsPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <WarehouseOperations />;
  return <ProtectedRoute Component={WarehouseOperations} />;
};

const getOptimizationPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <Optimization />;
  return <ProtectedRoute Component={Optimization} />;
};

const getOptimizationLegacyPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <OptimizationLegacy />;
  return <ProtectedRoute Component={OptimizationLegacy} />;
};

const getOptimizationNewPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <OptimizationNew />;
  return <ProtectedRoute Component={OptimizationNew} />;
};

const getOptimizationComparePage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <OptimizationCompare />;
  return <ProtectedRoute Component={OptimizationCompare} />;
};

const getLocationsPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <Locations />;
  return <ProtectedRoute Component={Locations} />;
};

const getPickingLabelsPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <PickingLabels />;
  return <ProtectedRoute Component={PickingLabels} />;
};

const getFulfillmentDeltasPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <FulfillmentDeltas />;
  return <ProtectedRoute Component={FulfillmentDeltas} />;
};

const ItemSearchPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <ItemSearch />;
  return <ProtectedRoute Component={ItemSearch} />;
};

const getFulfillmentDeltasPrintablePage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <FulfillmentDeltasPrintable />;
  return <ProtectedRoute Component={FulfillmentDeltasPrintable} />;
};

const getPackingListPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <PackingList />;
  return <ProtectedRoute Component={PackingList} />;
};

const getDeliveryNotesPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <DeliveryNotes />;
  return <ProtectedRoute Component={DeliveryNotes} />;
};

const getDeliveryOverridesPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <DeliveryOverrides />;
  return <ProtectedRoute Component={DeliveryOverrides} />;
};

const getCreateDeliveryOverridePage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <CreateDeliveryOverride />;
  return <ProtectedRoute Component={CreateDeliveryOverride} />;
};

const getDockBookingAdminPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <DockBookingAdmin />;
  return <ProtectedRoute Component={DockBookingAdmin} />;
};

const getProcessPathAdminPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <ProcessPathAdmin />;
  return <ProtectedRoute Component={ProcessPathAdmin} />;
};

const getPutawayReportPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <PutawayReport />;
  return <ProtectedRoute Component={PutawayReport} />;
};

const getPutawayAppPage = (): JSX.Element => (
  <ProtectedRoute Component={PutawayApp} preserveSearch />
);

const getPutawaySettingsPage = (): JSX.Element => (
  <ProtectedRoute Component={PutawaySettings} />
);

const getWarehouseLayoutPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <WarehouseLayout />;
  return <ProtectedRoute Component={WarehouseLayout} />;
};

const getWarehouseLayoutBuilderPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <WarehouseLayoutBuilder />;
  return <ProtectedRoute Component={WarehouseLayoutBuilder} />;
};

const getProcessPathSettingsPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <ProcessPathSettings />;
  return <ProtectedRoute Component={ProcessPathSettings} />;
};

const getProcessPathAssociatesSetupPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <ProcessPathAssociatesSetup />;
  return <ProtectedRoute Component={ProcessPathAssociatesSetup} />;
};

const getDriverSafetyAcknowledgementsPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <DriverSafetyAcknowledgements />;
  return <ProtectedRoute Component={DriverSafetyAcknowledgements} />;
};

const getCreateDriverSafetyAcknowledgementPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <CreateDriverSafetyAcknowledgement />;
  return <ProtectedRoute Component={CreateDriverSafetyAcknowledgement} />;
};

const getViewDriverSafetyAcknowledgementPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <ViewDriverSafetyAcknowledgement />;
  return <ProtectedRoute Component={ViewDriverSafetyAcknowledgement} />;
};

const getVehicleInspectionReportsPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <VehicleInspectionReports />;
  return (
    <ProtectedRoute
      Component={VehicleInspectionReports}
      allowedRoles={['admin']}
    />
  );
};

const getVehicleInspectionTruckPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <VehicleInspectionTruck />;
  return (
    <ProtectedRoute
      Component={VehicleInspectionTruck}
      allowedRoles={['admin']}
    />
  );
};

const getVehicleInspectionReportPage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true')
    return <VehicleInspectionReport />;
  return (
    <ProtectedRoute
      Component={VehicleInspectionReport}
      allowedRoles={['admin']}
    />
  );
};

const getStageGatePage = (): JSX.Element => {
  if (process.env.CYPRESS_AUTH_MOCKED === 'true') return <StageGate />;
  return <ProtectedRoute Component={StageGate} />;
};

const App = (): JSX.Element => {
  const { pathname } = useLocation();
  // /floor (kiosk tablet) and /scanner (handheld CT60) specifically — shared
  // devices passed between associates for brief, low-stakes interactions with
  // nothing meaningful to lose, so a crash is worth silently self-healing
  // rather than stranding whoever's holding the device on a manual reload.
  // Used to extend auto-reload to the outer boundary too (see below), not
  // just the page components themselves.
  const isSharedDeviceScreen =
    pathname.startsWith(processPathSelfAssignPublicPath) ||
    pathname.startsWith(processPathSwitchPublicPath);
  // External-driver / warehouse-TV pages: skip the internal app tree entirely
  // (no Google Maps script, no internal routes).
  const isPublicPage =
    pathname.startsWith('/inbound-appointment') ||
    pathname.startsWith('/vendor-portal') ||
    pathname.startsWith('/docks/') ||
    pathname.startsWith('/dock-checkin') ||
    isSharedDeviceScreen;
  const { email } = useAppSelector(authSelector);
  const {
    deliveryServiceDeliveryOverridesPage,
    deliveryServiceDriverSafetyAcknowledgementsPage,
    deliveryServiceVehicleInspectionReportsPage,
    deliveryServiceOptimizationScenarioComparison,
    deliveryServiceOptimizationScenarioManagement,
  } = useFlags();
  const stageGateEnabled = useOutboundGatesEnabled();
  const warehouseLayoutEnabled = useWarehouseLayoutBuilderEnabled();
  const { getAccessTokenSilently, loginWithRedirect, logout, user } =
    useAuth0();
  sec.setAccessTokenSilently(getAccessTokenSilently);
  sec.setLoginWithRedirect(loginWithRedirect);
  sec.setLogout(logout);

  // The installed PWA is the warehouse tool: lock it to the Putaway app so it
  // always lands there (not the /deliveries web default). Drivers get the same
  // lock in a browser, except on public pages: associates use the floor and
  // scanner screens too.
  const isPwa = isPwaContext();
  useLockToPutaway(isPwa || (isPutawayOnlyUser(user) && !isPublicPage));

  useEffect(() => {
    if (email) {
      // This means data captured will *not* be annonymous.
      // Rather than `indentify` assigning an annonymous id,
      // it will be tied to the user's email.
      // As this is an internal app, I believe this is OK / ideal.
      analytics.identify(email);
    }
  }, [email]);

  // A flag-gated route: the flag is undefined until LaunchDarkly resolves, so
  // hold the render rather than briefly redirecting a user who does have access.
  const flagGatedRoute = (
    isEnabled: boolean | undefined,
    page: () => JSX.Element,
    fallback: string = deliveryPath,
  ): JSX.Element => {
    if (isEnabled === undefined) return <div>Loading...</div>;
    if (!isEnabled) return <Navigate to={fallback} replace />;
    return page();
  };

  const getDriverSafetyAcknowledgementsRoute = (
    page: () => JSX.Element,
  ): JSX.Element =>
    flagGatedRoute(deliveryServiceDriverSafetyAcknowledgementsPage, page);

  const getVehicleInspectionReportsRoute = (
    page: () => JSX.Element,
  ): JSX.Element =>
    flagGatedRoute(deliveryServiceVehicleInspectionReportsPage, page);

  const getDeliveryOverridesRoute = (page: () => JSX.Element): JSX.Element =>
    flagGatedRoute(deliveryServiceDeliveryOverridesPage, page);

  const getStageGateRoute = (page: () => JSX.Element): JSX.Element =>
    flagGatedRoute(stageGateEnabled, page);

  const getWarehouseLayoutRoute = (page: () => JSX.Element): JSX.Element =>
    flagGatedRoute(warehouseLayoutEnabled, page);

  const getOptimizationManagementRoute = (
    page: () => JSX.Element,
  ): JSX.Element =>
    flagGatedRoute(
      deliveryServiceOptimizationScenarioManagement,
      page,
      optimizationPath,
    );

  const getOptimizationRootRoute = (): JSX.Element => {
    switch (
      getOptimizationRouteMode(
        deliveryServiceOptimizationScenarioManagement,
        deliveryServiceOptimizationScenarioComparison,
      )
    ) {
      case 'management':
        return getOptimizationPage();
      case 'legacy':
        return getOptimizationLegacyPage();
      case 'loading':
        return <div>Loading...</div>;
      case 'disabled':
        return <Navigate to={deliveryPath} replace />;
    }
  };

  return (
    <UrqlProvider value={client}>
      <main sx={mainStyle}>
        {/* Outside <Suspense> so it also catches a React.lazy chunk failing to
            load — a stale tab after a deploy — which Suspense itself doesn't.
            resetKey clears the fallback when the user navigates elsewhere.

            autoReload follows isSharedDeviceScreen: Navbar, the toasts, and
            the modal below are siblings of the floor/scanner routes, not
            children of them, so a throw from one of those never reaches the
            per-route "floor"/"scanner" boundaries — only this outer one.
            Without this, a crash in shared chrome while on /floor or
            /scanner would fall back to a manual Reload button, stranding
            whoever's holding the shared device instead of self-healing. */}
        <ErrorBoundary
          label="app"
          resetKey={pathname}
          autoReload={isSharedDeviceScreen}
        >
          {/* Blank while a page's code loads, like GoogleMapsScript below,
              not unstyled "Loading..." text the page then replaces. */}
          <Suspense fallback={<div />}>
            <AnalyticsTrackClicks>
              {/* Installed PWA is locked to Putaway, so the app nav is noise. */}
              {!isPwa && <Navbar />}
              <ToastNotification />
              <CustomModal />
              {/* Public pages for external drivers and warehouse TVs: no Auth0,
                their own urql client that attaches no auth headers (the
                booking token / dock id is the only auth), and rendered
                OUTSIDE LoadScript so a driver on a slow connection never
                waits on Google Maps to see the booking form. */}
              <Routes>
                <Route
                  path={dockBookingPublicPath}
                  element={
                    <UrqlProvider value={publicClient}>
                      <DockBooking />
                    </UrqlProvider>
                  }
                />
                <Route
                  path={vendorPortalPublicPath}
                  element={
                    <UrqlProvider value={publicClient}>
                      <VendorPortal />
                    </UrqlProvider>
                  }
                />
                <Route
                  path={dockDisplayPublicPath}
                  element={
                    <UrqlProvider value={publicClient}>
                      <DockDisplay />
                    </UrqlProvider>
                  }
                />
                {/* Driver check-in kiosk: a warehouse tablet with nobody
                  dedicated to babysit it between drivers, same self-heal need
                  as the two unattended screens below. */}
                <Route
                  path={dockCheckinPublicPath}
                  element={
                    <ErrorBoundary label="dock-checkin" autoReload>
                      <UrqlProvider value={publicClient}>
                        <DockCheckin />
                      </UrqlProvider>
                    </ErrorBoundary>
                  }
                />
                {/* Warehouse-wide entry point for the printed placard — same
                  page component, scoped by warehouseId instead of dockId. */}
                <Route
                  path={dockCheckinWarehousePublicPath}
                  element={
                    <ErrorBoundary label="dock-checkin-warehouse" autoReload>
                      <UrqlProvider value={publicClient}>
                        <DockCheckin />
                      </UrqlProvider>
                    </ErrorBoundary>
                  }
                />
                {/* Ops "Departed" screen: same tablet as the driver kiosk
                  above, a different screen, one per physical dock. The
                  warehouse route below is the fallback for when a specific
                  door's kiosk is down — same page component, scoped by
                  warehouseId instead of dockId. */}
                <Route
                  path={dockDepartedPublicPath}
                  element={
                    <ErrorBoundary label="dock-departed" autoReload>
                      <UrqlProvider value={publicClient}>
                        <DockDeparted />
                      </UrqlProvider>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path={dockDepartedWarehousePublicPath}
                  element={
                    <ErrorBoundary label="dock-departed-warehouse" autoReload>
                      <UrqlProvider value={publicClient}>
                        <DockDeparted />
                      </UrqlProvider>
                    </ErrorBoundary>
                  }
                />
                {/* The two unattended warehouse screens. Nobody is standing in
                  front of these to press a button, and /floor's own self-heal
                  reload lives inside the page component — so a render throw
                  kills the recovery along with the screen. These boundaries
                  restart the device themselves (bounded; see ErrorBoundary). */}
                <Route
                  path={processPathSelfAssignPublicPath}
                  element={
                    <ErrorBoundary label="floor" autoReload>
                      <UrqlProvider value={publicClient}>
                        <ProcessPathSelfAssign />
                      </UrqlProvider>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path={processPathSwitchPublicPath}
                  element={
                    <ErrorBoundary label="scanner" autoReload>
                      <UrqlProvider value={publicClient}>
                        <ProcessPathSwitch />
                      </UrqlProvider>
                    </ErrorBoundary>
                  }
                />
                {/* Everything else is handled by the LoadScript tree below. */}
                <Route path="*" element={null} />
              </Routes>
              {!isPublicPage && (
                <GoogleMapsScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                  <Routes>
                    <Route path={loginPath} element={<Login />} />
                    <Route
                      path={dockBookingsPath}
                      element={getDockBookingAdminPage()}
                    />
                    <Route
                      path={processPathSettingsPath}
                      element={getProcessPathSettingsPage()}
                    />
                    <Route
                      path={processPathAssociatesSetupPath}
                      element={getProcessPathAssociatesSetupPage()}
                    />
                    <Route
                      path={processPathsPath}
                      element={getProcessPathAdminPage()}
                    />
                    <Route
                      path={deliveryPath}
                      element={getRouteManagementPage()}
                    />
                    <Route path={analyticsPath} element={getAnalyticsPage()} />
                    <Route path={issuesPath} element={getIssuesPage()} />
                    <Route
                      path={invoiceByDeliveryPath}
                      element={getInvoiceByDeliveryPage()}
                    />
                    <Route
                      path={invoiceByDeliveryDetailPath}
                      element={getInvoiceByDeliveryDetailPage()}
                    />
                    <Route
                      path={warehouseOperationsPath}
                      element={getWarehouseOperationsPage()}
                    />
                    <Route
                      path={optimizationPath}
                      element={getOptimizationRootRoute()}
                    />
                    <Route
                      path={optimizationNewPath}
                      element={getOptimizationManagementRoute(
                        getOptimizationNewPage,
                      )}
                    />
                    <Route
                      path={optimizationComparePath}
                      element={getOptimizationManagementRoute(
                        getOptimizationComparePage,
                      )}
                    />
                    <Route
                      path={vendorReceivingPath}
                      element={getVendorReceivingPage()}
                    />
                    <Route
                      path={stageGatePath}
                      element={getStageGateRoute(getStageGatePage)}
                    />
                    <Route
                      path={putawayReportPath}
                      element={getPutawayReportPage()}
                    />
                    <Route
                      path={putawayAppPath}
                      element={getPutawayAppPage()}
                    />
                    <Route
                      path={`${putawayAppPath}/task/:taskId`}
                      element={getPutawayAppPage()}
                    />
                    <Route
                      path={putawaySettingsPath}
                      element={getPutawaySettingsPage()}
                    />
                    <Route
                      path={warehouseLayoutPath}
                      element={getWarehouseLayoutRoute(getWarehouseLayoutPage)}
                    />
                    <Route
                      path={warehouseLayoutBuilderPath}
                      element={getWarehouseLayoutRoute(
                        getWarehouseLayoutBuilderPage,
                      )}
                    />
                    <Route path={locationsPath} element={getLocationsPage()} />
                    <Route
                      path={`${pickingLabelsPath}/:warehouseId/:warehouseName/:nightEnd`}
                      element={getPickingLabelsPage()}
                    />
                    <Route
                      path={fulfillmentDeltasPath}
                      element={getFulfillmentDeltasPage()}
                    />
                    <Route
                      path={`${fulfillmentDeltasPrintablePath}/:warehouseId/:nightEnd`}
                      element={getFulfillmentDeltasPrintablePage()}
                    />
                    <Route path={itemSearchPath} element={ItemSearchPage()} />
                    <Route
                      path={packingListPath}
                      element={getPackingListPage()}
                    />
                    <Route
                      path={deliveryNotesPath}
                      element={getDeliveryNotesPage()}
                    />
                    <Route
                      path={deliveryOverridesPath}
                      element={getDeliveryOverridesRoute(
                        getDeliveryOverridesPage,
                      )}
                    />
                    <Route
                      path={deliveryOverridesCreatePath}
                      element={getDeliveryOverridesRoute(
                        getCreateDeliveryOverridePage,
                      )}
                    />
                    <Route
                      path={driverSafetyAcknowledgementsPath}
                      element={getDriverSafetyAcknowledgementsRoute(
                        getDriverSafetyAcknowledgementsPage,
                      )}
                    />
                    <Route
                      path={driverSafetyAcknowledgementsCreatePath}
                      element={getDriverSafetyAcknowledgementsRoute(
                        getCreateDriverSafetyAcknowledgementPage,
                      )}
                    />
                    <Route
                      path={driverSafetyAcknowledgementsDetailPath}
                      element={getDriverSafetyAcknowledgementsRoute(
                        getViewDriverSafetyAcknowledgementPage,
                      )}
                    />
                    <Route
                      path={vehicleInspectionReportsPath}
                      element={getVehicleInspectionReportsRoute(
                        getVehicleInspectionReportsPage,
                      )}
                    />
                    <Route
                      path={vehicleInspectionTruckPath}
                      element={getVehicleInspectionReportsRoute(
                        getVehicleInspectionTruckPage,
                      )}
                    />
                    <Route
                      path={vehicleInspectionReportPath}
                      element={getVehicleInspectionReportsRoute(
                        getVehicleInspectionReportPage,
                      )}
                    />
                    <Route path="/" element={<Login />} />
                  </Routes>
                </GoogleMapsScript>
              )}
            </AnalyticsTrackClicks>
          </Suspense>
        </ErrorBoundary>
      </main>
    </UrqlProvider>
  );
};

export default App;
